import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from '@lt/localization';

import {
  HotelExperienceCard,
  HotelExperienceExpanded,
  HotelStars,
  Slider,
} from '@lt/components';

import Container from '../../components/Container';

const { HotelExperiencePropTypes } = HotelExperienceCard;

const createMarkup = (encodedHtml) => ({
  __html: _.unescape(encodedHtml),
});

const HotelExperienceList = ({
  experiences,
  hotelName,
  hotelStars,
  isMobile,
  scrollToSectionTop,
  ...props
}) => {
  const { t } = useTranslation();
  const [index, setIndex] = useState(null);
  const [isCollapsing, setCollapsing] = useState(false);
  const expandedExperience = experiences[index];

  const changeSlide = (newIndex) => {
    setCollapsing(true);
    setTimeout(() => {
      setIndex(newIndex);
      setCollapsing(false);
      scrollToSectionTop();
    }, 400);
  };

  return (
    !_.isEmpty(experiences) && (
      <StyledHotelExperienceList isMobile={isMobile} {...props}>
        <StyledContainer isMobile={isMobile} fullWidth={isMobile}>
          <StyledTitle>
            {t('hotel.experienceMobile')}
            <StyledHotelName>
              <StyledLabel dangerouslySetInnerHTML={createMarkup(hotelName)} />
              {!isMobile && <StyledHotelStars value={hotelStars} size={11} />}
            </StyledHotelName>
          </StyledTitle>
        </StyledContainer>
        {expandedExperience && (
          <StyledExpanderContainer
            white
            isMobile={isMobile}
            fullWidth
            isCollapsing={isCollapsing}
          >
            <StyledContainer fullWidth={isMobile} isMobile={isMobile}>
              <HotelExperienceExpanded
                isMobile={isMobile}
                onCollapse={() => changeSlide(null)}
                {...expandedExperience}
              />
            </StyledContainer>
          </StyledExpanderContainer>
        )}
        <StyledContainer isMobile={isMobile} fullWidth={isMobile}>
          <Slider
            controls={!isMobile}
            dots={!isMobile}
            gap={isMobile ? 8 : 20}
            offset={10}
          >
            {experiences.map((item, i) => (
              <StyledHotelExperienceCard
                key={item.title}
                isMobile={isMobile}
                onExpand={() => changeSlide(i)}
                data-hidden={i === index}
                {...item}
              />
            ))}
          </Slider>
        </StyledContainer>
      </StyledHotelExperienceList>
    )
  );
};

HotelExperienceList.propTypes = {
  className: PropTypes.string,
  experiences: PropTypes.arrayOf(HotelExperiencePropTypes),
  hotelName: PropTypes.string,
  hotelStars: PropTypes.number,
  isMobile: PropTypes.bool,
  scrollToSectionTop: PropTypes.func,
};

HotelExperienceList.defaultProps = {
  className: '',
  experiences: [],
  hotelName: null,
  hotelStars: null,
  isMobile: false,
  scrollToSectionTop: Function.prototype,
};

const StyledContainer = styled(Container)`
  margin-bottom: 0;
`;

const StyledExpanderContainer = styled(StyledContainer)`
  ${({ isMobile, isCollapsing }) => `
    margin-top: 10px;
    animation: ${!isMobile && isCollapsing ? 'unmount' : 'mount'} .45s linear;

    @keyframes mount {
      0% {
        background: transparent;
        height: 0;
        margin-bottom: 0;
        opacity: 0;
        transform: translate3d(0, 70px, 0);
      }
      50% {
        opacity: 0;
        height: 420px;
      }
    }
  
    @keyframes unmount {
      20% {
        padding-top: 200px;
      }

      70%, 100% {
        background: transparent;
      }

      20%, 100% {
        height: 0;
        margin-bottom: 0;
        margin-top: 0;
        opacity: 0;
      }
    }
  `}
`;

const StyledTitle = styled.h2`
  font-weight: bold;
`;

const StyledHotelName = styled.span`
  font-weight: normal;
`;

const StyledLabel = styled.span`
  font-weight: normal;
`;

const StyledHotelStars = styled(HotelStars)`
  margin-left: 7px;
  display: inline;
`;

const StyledHotelExperienceCard = styled(HotelExperienceCard)``;

const StyledHotelExperienceList = styled(Container)`
  ${({ theme, isMobile }) => `
    padding: 0;
    margin-bottom: ${isMobile ? 10 : 20}px;
    ${StyledTitle} {
      ${theme.typography[isMobile ? 'headline3' : 'headline2']}
      color: ${theme.colors.blackBase};
      margin: ${isMobile ? '11px 10px 0' : '19px 10px 0'};
      padding-top: 10px;
    }

    ${StyledHotelName} {
      display: ${isMobile ? 'inline' : 'block'};
      margin-left: ${isMobile ? 7 : 0}px;
    }

    ${StyledHotelExperienceCard} {
      margin-top: ${isMobile ? 10 : 20}px;
    }
  `}
`;

export default HotelExperienceList;
